/* eslint-disable react/no-danger */
import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import logo from 'images/logo.svg';
import messages from './messages';

import { FormattedMessage } from 'react-intl';

import './header.style.css';
import { FEATURES } from 'utils/permissions';
import { BellOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import session from 'containers/App/session';
import { Badge, Button, Modal, notification } from 'antd';
import { ALERT_PRIORITY } from 'right-revenue-microservice-sdk/lib/constants';

const NotificationWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    height: 100%;
    padding-top: 10px;
    .anticon {
        font-size: 24px;
        vertical-align: middle;
        color: #fff;
    }
`;

const RECONNECT_INTERVAL = 30000; // 30 seconds

const header = ({ user, authorisedFeatures }) => {
    const token = useSelector(session.selectors.selectUserToken);
    const propertyId = useSelector(session.selectors.selectSelectedPropertyId);
    const [notificationCount, setNotificationCount] = useState(0);
    const wsRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const isMounted = useRef(false);
    const navigate = useNavigate();
    const [api, notificationContextHolder] = notification.useNotification();
    const [modal, modalContextHolder] = Modal.useModal();
    const shownAlertUUIDs = useRef(new Set());

    const acknowledgeNotification = (notificationUUID) => {
        shownAlertUUIDs.current.delete(notificationUUID);
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(
                JSON.stringify({
                    type: 'acknowledgeNotification',
                    uuid: notificationUUID,
                }),
            );
        }
    };

    const renderHtml = (maybeHtml) => {
        if (typeof maybeHtml !== 'string') return maybeHtml;
        // convert plain newlines to <br/> then sanitize
        const htmlWithBreaks = maybeHtml.replace(/\r\n|\n/g, '<br/>');
        const clean = DOMPurify.sanitize(htmlWithBreaks);
        return <div dangerouslySetInnerHTML={{ __html: clean }} />;
    };

    const openNotification = (uuid, title, body, alertPriority) => {
        const type = alertPriority === ALERT_PRIORITY.MEDIUM ? 'warning' : 'info';
        const duration = alertPriority === ALERT_PRIORITY.MEDIUM ? 0 : 10;
        api[type]({
            message: title,
            description: renderHtml(body),
            placement: 'topRight',
            duration,
            onClick: () => { navigate('/app/notifications', { state: { preselected: uuid } }); },
            ...(alertPriority === ALERT_PRIORITY.MEDIUM && {
                onClose: () => acknowledgeNotification(uuid),
            }),
        });
    };

    const openModalNotification = (uuid, title, body) => {
        modal.error({
            title,
            content: renderHtml(body),
            footer: (
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                    <Button
                        key="view"
                        onClick={() => {
                            navigate('/app/notifications', { state: { preselected: uuid } });
                            Modal.destroyAll(); // close modal(s)
                        }}
                    >
                        <FormattedMessage {...messages.alertModalViewButtonText} />
                    </Button>
                    <Button
                        key="close"
                        type="primary"
                        onClick={() => {
                            acknowledgeNotification(uuid);
                            Modal.destroyAll();
                        }}
                    >
                        <FormattedMessage {...messages.alertModalCloseButtonText} />
                    </Button>
                </div>
            ),
            centered: true,
            maskClosable: false,
            closable: false,
            className: 'notification-modal',
        });
    };

    const connectWebSocket = React.useCallback(() => {
        const serverUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000';
        const isSecure = serverUrl.startsWith('https');
        const urlWithoutProtocol = serverUrl.replace(/^https?:\/\//, '');
        const wsUrl = `${isSecure ? 'wss' : 'ws'}://${urlWithoutProtocol}?token=${token}&propertyID=${propertyId}`;
        wsRef.current = new window.WebSocket(wsUrl);

        wsRef.current.onopen = () => {
            // Optionally send a handshake or authentication message here
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }
        };

        wsRef.current.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                if (data.type === 'notificationCount') {
                    setNotificationCount(data.count);
                }
                if (data.type === 'showAlert') {
                    // Only show if this alert UUID hasn't been shown yet
                    if (!shownAlertUUIDs.current.has(data.uuid)) {
                        shownAlertUUIDs.current.add(data.uuid);
                        const { uuid, title, body, alertPriority } = data;
                        switch (alertPriority) {
                            case ALERT_PRIORITY.HIGH:
                                openModalNotification(uuid, title, body);
                                break;
                            default:
                                openNotification(uuid, title, body, alertPriority);
                                break;
                        }
                    }
                }
            } catch (e) {
                // Ignore invalid messages
            }
        };

        wsRef.current.onerror = (err) => {
            // If connection cannot be established, attempt to reconnect
            const msg = err?.message || '';
            if (
                msg.includes('establish') || // covers "Cannot establish a connection" and similar
                (err &&
                    err.target &&
                    (err.target.readyState === WebSocket.CLOSED ||
                        err.target.readyState === WebSocket.CLOSING))
            ) {
                if (isMounted.current && !reconnectTimeoutRef.current) {
                    reconnectTimeoutRef.current = setTimeout(() => {
                        connectWebSocket();
                    }, RECONNECT_INTERVAL);
                }
            }
        };

        wsRef.current.onclose = () => {
            // Attempt to reconnect after a delay
            if (isMounted.current && !reconnectTimeoutRef.current) {
                reconnectTimeoutRef.current = setTimeout(() => {
                    connectWebSocket();
                }, RECONNECT_INTERVAL);
            }
        };
    }, [token, propertyId]);

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
            if (wsRef.current) {
                wsRef.current.close();
                wsRef.current = null;
            }
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        // Only connect/disconnect when authorisedFeatures changes and is loaded
        if (authorisedFeatures.includes(FEATURES.viewNotifications)) {
            connectWebSocket();
        } else {
            // If user loses permission, disconnect websocket
            if (wsRef.current) {
                wsRef.current.close();
                wsRef.current = null;
            }
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }
        }
    }, [authorisedFeatures]);

    useEffect(() => {
        // On propertyId change, send message to websocket if open
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(
                JSON.stringify({
                    type: 'setProperty',
                    propertyID: propertyId,
                }),
            );
        }
    }, [propertyId]);

    const permissions = {
        cancellationReport: authorisedFeatures.includes(FEATURES.cancellationReport),
        bookingsReport: authorisedFeatures.includes(FEATURES.bookingsReport),
        comparisonReport: authorisedFeatures.includes(FEATURES.comparisonReport),
        segmentationReport: authorisedFeatures.includes(FEATURES.segmentationReport),
        groupDisplacementCalculator: authorisedFeatures.includes(FEATURES.groupDisplacementCalculator),
        costOfOccupiedRoomCalculator: authorisedFeatures.includes(FEATURES.costOfOccupiedRoomCalculator),
        rateCalculator: authorisedFeatures.includes(FEATURES.rateCalculator),
        commissionReport: authorisedFeatures.includes(FEATURES.commissionReport),
        cardinalRecommendations: authorisedFeatures.includes(FEATURES.cardinalRecommendations),
        pickupReportV2: authorisedFeatures.includes(FEATURES.pickupReportV2),
        segmentForecast: authorisedFeatures.includes(FEATURES.segmentForecast),
        sellingRateReport: authorisedFeatures.includes(FEATURES.sellingRateReport),
        rateChangeImpact: authorisedFeatures.includes(FEATURES.rateChangeImpact),
        financeHub: authorisedFeatures.includes(FEATURES.financeHub),
        financeHubBusinessChange: authorisedFeatures.includes(FEATURES.financeHubBusinessChange),
        financeHubSegment: authorisedFeatures.includes(FEATURES.financeHubSegment),
        financeHubSegmentV2: authorisedFeatures.includes(FEATURES.financeHubSegmentV2),
        financeHubSummary: authorisedFeatures.includes(FEATURES.financeHubSummary),
        financeHubMonthlyTotals: authorisedFeatures.includes(FEATURES.financeHubMonthlyTotals),
        budgetCalculator: authorisedFeatures.includes(FEATURES.budgetCalculator),
        dashboardV2: authorisedFeatures.includes(FEATURES.dashboardV2),
        fiscalBreakdownReport: authorisedFeatures.includes(FEATURES.fiscalBreakdownReport),
        recommendationReport: authorisedFeatures.includes(FEATURES.recommendationReport),
        otaParity: authorisedFeatures.includes(FEATURES.otaParity),
        groupPickupReport: authorisedFeatures.includes(FEATURES.groupPickupReport),
        settingsCalendar: authorisedFeatures.includes(FEATURES.settingsCalendar),
        sellingRateMassUpdate: authorisedFeatures.includes(FEATURES.sellingRateMassUpdate),
        segmentBucketManagement: authorisedFeatures.includes(FEATURES.segmentBucketManagement),
        otaCompsets: authorisedFeatures.includes(FEATURES.otaCompsets),
        scheduledEmailsManagement: authorisedFeatures.includes(FEATURES.scheduledEmailsManagement),
        roomManagement: authorisedFeatures.includes(FEATURES.roomManagement),
        propertyManagement: authorisedFeatures.includes(FEATURES.propertyManagement),
        customisedReport: authorisedFeatures.includes(FEATURES.customisedReport),
        events: authorisedFeatures.includes(FEATURES.events),
        statistics: authorisedFeatures.includes(FEATURES.statistics),
        revflow: authorisedFeatures.includes(FEATURES.revflow),
        viewAeRecs: authorisedFeatures.includes(FEATURES.viewAERecs),
        hurdleRackAdvisor: authorisedFeatures.includes(FEATURES.hurdleRackAdvisor),
        rateCodeManagement: authorisedFeatures.includes(FEATURES.rateCodeManagement),
        viewNotifications: authorisedFeatures.includes(FEATURES.viewNotifications),
        componentRoomsManagement: authorisedFeatures.includes(FEATURES.componentRoomsManagement),
        budgetCreator: authorisedFeatures.includes(FEATURES.budgetCreator),
    };

    return (
        <>
            {modalContextHolder}
            {notificationContextHolder}
            <div className="Header">
                <nav className="HeaderNav">
                    <ul className="HeaderMenu">
                        <li className="HeaderMenuLogo">
                            <img src={logo} width="203px" height="45px" alt="Right Revenue Logo" />
                        </li>
                        <li className="HeaderMenuItem">
                            <NavLink to="/app/dashboard" className="HeaderMenuItemNavLink">
                                <FormattedMessage {...messages.dashboardLink} />
                            </NavLink>
                        </li>
                        {permissions.dashboardV2 && (
                            <li className="HeaderMenuItem">
                                <NavLink to="/app/dashboard/v2" className="HeaderMenuItemNavLink">
                                    <FormattedMessage {...messages.dashboardV2Link} />
                                </NavLink>
                            </li>
                        )}
                        {permissions.revflow && (
                            <li className="HeaderMenuItem">
                                <NavLink to="/app/revflow" className="HeaderMenuItemNavLink">
                                    <FormattedMessage {...messages.revflowLink} />
                                </NavLink>
                            </li>
                        )}
                        <li className="HeaderMenuItem">
                            <div className="HeaderMenuItemNavLink">
                                <FormattedMessage {...messages.overviewLink} />
                            </div>
                            <ul className="HeaderMenuSubWide">
                                <li className="HeaderMenuItemSub">
                                    <Link to="/app/yearly" className="HeaderMenuItemNavLink">
                                        <FormattedMessage {...messages.yearOverviewLink} />
                                    </Link>
                                </li>
                                <li className="HeaderMenuItemSub">
                                    <Link to="/app/monthly" className="HeaderMenuItemNavLink">
                                        <FormattedMessage {...messages.monthOverviewLink} />
                                    </Link>
                                </li>
                                <li className="HeaderMenuItemSub">
                                    <Link to="/app/daily" className="HeaderMenuItemNavLink">
                                        <div>
                                            <FormattedMessage {...messages.dayOverviewLink} />
                                        </div>
                                    </Link>
                                </li>
                            </ul>
                        </li>
                        <li className="HeaderMenuItem">
                            <div className="HeaderMenuItemNavLink">Rates Management</div>
                            <ul className="HeaderMenuSubWide">
                                {permissions.recommendations && (
                                    <li className="HeaderMenuItemSub">
                                        <Link to="/app/recommendations" className="HeaderMenuItemNavLink">
                                            <FormattedMessage {...messages.recommendationsLink} />
                                        </Link>
                                    </li>
                                )}
                                {permissions.recommendationReport && (
                                    <li className="HeaderMenuItemSub">
                                        <Link to="/app/reports/recommendationReport" className="HeaderMenuItemNavLink">
                                            <FormattedMessage {...messages.recommendationReportLink} />
                                        </Link>
                                    </li>
                                )}
                                {(permissions.cardinalRecommendations || permissions.viewAeRecs) && (
                                    <li className="HeaderMenuItemSub">
                                        <Link to="/app/sellingRateCalendar" className="HeaderMenuItemNavLink">
                                            <div>
                                                <FormattedMessage {...messages.cardinalRateCalendarLink} />
                                            </div>
                                        </Link>
                                    </li>
                                )}
                                {permissions.otaParity && (
                                    <li className="HeaderMenuItemSub">
                                        <Link to="/app/otaParity" className="HeaderMenuItemNavLink">
                                            <div>
                                                <FormattedMessage {...messages.otaParityLink} />
                                            </div>
                                        </Link>
                                    </li>
                                )}
                            </ul>
                        </li>
                        <li className="HeaderMenuItem">
                            <div className="HeaderMenuItemNavLink">Reports</div>
                            <ul className="HeaderMenuSubWide">
                                {permissions.cancellationReport && (
                                    <li className="HeaderMenuItemSub">
                                        <Link to="/app/reports/cancellationReport" className="HeaderMenuItemNavLink">
                                            <FormattedMessage {...messages.cancellationReportLink} />
                                        </Link>
                                    </li>
                                )}
                                {permissions.bookingsReport && (
                                    <li className="HeaderMenuItemSub">
                                        <Link to="/app/reports/bookingsReport" className="HeaderMenuItemNavLink">
                                            <FormattedMessage {...messages.reservationReportLink} />
                                        </Link>
                                    </li>
                                )}
                                {permissions.comparisonReport && (
                                    <li className="HeaderMenuItemSub">
                                        <Link to="/app/reports/comparisonReport" className="HeaderMenuItemNavLink">
                                            <FormattedMessage {...messages.comparisonReportLink} />
                                        </Link>
                                    </li>
                                )}
                                {permissions.segmentationReport && (
                                    <li className="HeaderMenuItemSub">
                                        <Link to="/app/reports/segmentationReport" className="HeaderMenuItemNavLink">
                                            <FormattedMessage {...messages.segmentationReportLink} />
                                        </Link>
                                    </li>
                                )}
                                {permissions.fiscalBreakdownReport && (
                                    <li className="HeaderMenuItemSub">
                                        <Link to="/app/reports/fiscalBreakdownReport" className="HeaderMenuItemNavLink">
                                            <FormattedMessage {...messages.fiscalBreakdownReportLink} />
                                        </Link>
                                    </li>
                                )}
                                {permissions.pickupReportV2 && (
                                    <li className="HeaderMenuItemSub">
                                        <Link to="/app/reports/pickupReportV2" className="HeaderMenuItemNavLink">
                                            <FormattedMessage {...messages.pickupReportV2Link} />
                                        </Link>
                                    </li>
                                )}
                                {permissions.segmentForecast && (
                                    <li className="HeaderMenuItemSub">
                                        <Link to="/app/reports/pickupReportV3" className="HeaderMenuItemNavLink">
                                            <FormattedMessage {...messages.pickupReportV3Link} />
                                        </Link>
                                    </li>
                                )}
                                {permissions.commissionReport && (
                                    <li className="HeaderMenuItemSub">
                                        <Link to="/app/reports/commissionReport" className="HeaderMenuItemNavLink">
                                            <FormattedMessage {...messages.commissionReportLink} />
                                        </Link>
                                    </li>
                                )}
                                {permissions.sellingRateReport && (
                                    <li className="HeaderMenuItemSub">
                                        <Link to="/app/reports/sellingRateReport" className="HeaderMenuItemNavLink">
                                            <FormattedMessage {...messages.sellingRateReportLink} />
                                        </Link>
                                    </li>
                                )}
                                {permissions.groupPickupReport && (
                                    <li className="HeaderMenuItemSub">
                                        <Link to="/app/reports/groupPickupReport" className="HeaderMenuItemNavLink">
                                            <FormattedMessage {...messages.groupPickupReportLink} />
                                        </Link>
                                    </li>
                                )}
                                {permissions.customisedReport && (
                                    <li className="HeaderMenuItemSub">
                                        <Link to="/app/reports/customised" className="HeaderMenuItemNavLink">
                                            <FormattedMessage {...messages.customisedReportLink} />
                                        </Link>
                                    </li>
                                )}
                                {permissions.statistics && (
                                    <li className="HeaderMenuItemSub">
                                        <Link to="/app/reports/stats" className="HeaderMenuItemNavLink">
                                            <FormattedMessage {...messages.statisticsLink} />
                                        </Link>
                                    </li>
                                )}
                            </ul>
                        </li>
                        <li className="HeaderMenuItem">
                            <div className="HeaderMenuItemNavLink">Calculate Hub</div>
                            <ul className="HeaderMenuSubWide">
                                {permissions.costOfOccupiedRoomCalculator && (
                                    <li className="HeaderMenuItemSub">
                                        <Link to="/app/tools/occupied-room-cost" className="HeaderMenuItemNavLink">
                                            <FormattedMessage {...messages.costCalculatorLink} />
                                        </Link>
                                    </li>
                                )}
                                {permissions.rateCalculator && (
                                    <li className="HeaderMenuItemSub">
                                        <Link to="/app/tools/rate-calculator" className="HeaderMenuItemNavLink">
                                            <FormattedMessage {...messages.profitPredictorLink} />
                                        </Link>
                                    </li>
                                )}
                                {permissions.groupDisplacementCalculator && (
                                    <li className="HeaderMenuItemSub">
                                        <Link to="/app/tools/group-displacement" className="HeaderMenuItemNavLink">
                                            <FormattedMessage {...messages.groupDispLink} />
                                        </Link>
                                    </li>
                                )}
                                {permissions.rateChangeImpact && (
                                    <li className="HeaderMenuItemSub">
                                        <Link to="/app/tools/rate-change-impact" className="HeaderMenuItemNavLink">
                                            <FormattedMessage {...messages.rateChangeImpactLink} />
                                        </Link>
                                    </li>
                                )}
                            </ul>
                        </li>
                        {permissions.financeHub && (
                            <li className="HeaderMenuItem">
                                <div className="HeaderMenuItemNavLink">
                                    <FormattedMessage {...messages.financeHubLink} />
                                </div>
                                <ul className="HeaderMenuSubWide">
                                    {permissions.financeHubBusinessChange && (
                                        <li className="HeaderMenuItemSub">
                                            <Link to="/app/financehub/businesschange" className="HeaderMenuItemNavLink">
                                                <FormattedMessage {...messages.financeHubBusinessChangeLink} />
                                            </Link>
                                        </li>
                                    )}
                                    {permissions.financeHubSummary && (
                                        <li className="HeaderMenuItemSub">
                                            <Link to="/app/financehub/summary" className="HeaderMenuItemNavLink">
                                                <FormattedMessage {...messages.financeHubSummaryLink} />
                                            </Link>
                                        </li>
                                    )}
                                    {permissions.financeHubMonthlyTotals && (
                                        <li className="HeaderMenuItemSub">
                                            <Link to="/app/financehub/monthlytotals" className="HeaderMenuItemNavLink">
                                                <FormattedMessage {...messages.financeHubMonthlyTotalsLink} />
                                            </Link>
                                        </li>
                                    )}
                                    {permissions.financeHubSegment && (
                                        <li className="HeaderMenuItemSub">
                                            <Link to="/app/financehub/segmentation" className="HeaderMenuItemNavLink">
                                                <FormattedMessage {...messages.financeHubSegmentationLink} />
                                            </Link>
                                        </li>
                                    )}
                                    {permissions.financeHubSegmentV2 && (
                                        <li className="HeaderMenuItemSub">
                                            <Link to="/app/financehub/segmentationV2" className="HeaderMenuItemNavLink">
                                                <FormattedMessage {...messages.financeHubSegmentationV2Link} />
                                            </Link>
                                        </li>
                                    )}
                                    {permissions.budgetCalculator && (
                                        <li className="HeaderMenuItemSub">
                                            <NavLink to="/app/budgetCalculator" className="HeaderMenuItemNavLink">
                                                <FormattedMessage {...messages.budgetCalculatorLink} />
                                            </NavLink>
                                        </li>
                                    )}
                                    {permissions.budgetCreator && (
                                        <li className="HeaderMenuItemSub">
                                            <NavLink to="/app/budgetBuilder" className="HeaderMenuItemNavLink">
                                                <FormattedMessage {...messages.budgetCreatorLink} />
                                            </NavLink>
                                        </li>
                                    )}
                                </ul>
                            </li>
                        )}
                        <li className="HeaderMenuItem">
                            <div className="HeaderMenuItemNavLink">
                                <FormattedMessage {...messages.settingsMainLink} />
                            </div>
                            <ul className="HeaderMenuSubWide">
                                {permissions.settingsCalendar && (
                                    <li className="HeaderMenuItemSub">
                                        <Link to="/settings/calendar" className="HeaderMenuItemNavLink">
                                            <FormattedMessage {...messages.settingsCalendarLink} />
                                        </Link>
                                    </li>
                                )}
                                <li className="HeaderMenuItemSub">
                                    <Link to="/settings/bulkupdate" className="HeaderMenuItemNavLink">
                                        <FormattedMessage {...messages.settingsBulkUpdateLink} />
                                    </Link>
                                </li>
                                {permissions.sellingRateMassUpdate && (
                                    <li className="HeaderMenuItemSub">
                                        <Link to="/settings/sellingRateMassUpdate" className="HeaderMenuItemNavLink">
                                            <FormattedMessage {...messages.settingsSellingRateMassUpdateLink} />
                                        </Link>
                                    </li>
                                )}
                                {permissions.segmentBucketManagement && (
                                    <li className="HeaderMenuItemSub">
                                        <Link to="/settings/segmentBuckets" className="HeaderMenuItemNavLink">
                                            <FormattedMessage {...messages.settingsSegmentBucketsLink} />
                                        </Link>
                                    </li>
                                )}
                                {permissions.otaCompsets && (
                                    <li className="HeaderMenuItemSub">
                                        <Link to="/settings/otaCompSets" className="HeaderMenuItemNavLink">
                                            <FormattedMessage {...messages.settingsOtaCompsetsLink} />
                                        </Link>
                                    </li>
                                )}
                                {permissions.scheduledEmailsManagement && (
                                    <li className="HeaderMenuItemSub">
                                        <Link to="/settings/scheduledEmails" className="HeaderMenuItemNavLink">
                                            <FormattedMessage {...messages.settingsScheduledReportsLink} />
                                        </Link>
                                    </li>
                                )}
                                {permissions.roomManagement && (
                                    <li className="HeaderMenuItemSub">
                                        <Link to="/settings/rooms" className="HeaderMenuItemNavLink">
                                            <FormattedMessage {...messages.settingsRoomsLink} />
                                        </Link>
                                    </li>
                                )}
                                {permissions.propertyManagement && (
                                    <li className="HeaderMenuItemSub">
                                        <Link to="/settings/properties" className="HeaderMenuItemNavLink">
                                            <FormattedMessage {...messages.settingsPropertiesLink} />
                                        </Link>
                                    </li>
                                )}
                                {permissions.events && (
                                    <li className="HeaderMenuItemSub">
                                        <Link to="/settings/events" className="HeaderMenuItemNavLink">
                                            <FormattedMessage {...messages.settingsEventsLink} />
                                        </Link>
                                    </li>
                                )}
                                {permissions.hurdleRackAdvisor && (
                                    <li className="HeaderMenuItemSub">
                                        <Link to="/settings/hurdleRackAdvisor" className="HeaderMenuItemNavLink">
                                            <FormattedMessage {...messages.hurdleRackAdvisorLink} />
                                        </Link>
                                    </li>
                                )}
                                {permissions.rateCodeManagement && (
                                    <li className="HeaderMenuItemSub">
                                        <Link to="/settings/rateCodes" className="HeaderMenuItemNavLink">
                                            <FormattedMessage {...messages.rateCodeManagementLink} />
                                        </Link>
                                    </li>
                                )}
                                {permissions.componentRoomsManagement && (
                                    <li className="HeaderMenuItemSub">
                                        <Link to="/settings/componentRooms" className="HeaderMenuItemNavLink">
                                            <FormattedMessage {...messages.componentRoomsManagementLink} />
                                        </Link>
                                    </li>
                                )}
                            </ul>
                        </li>
                        {user.userId === 1 && (
                            <li className="HeaderMenuItem">
                                <div className="HeaderMenuItemNavLink">
                                    <FormattedMessage {...messages.adminLink} />
                                </div>
                                <ul className="HeaderMenuSubWide">
                                    <li className="HeaderMenuItemSub">
                                        <Link to="/admin/properties" className="HeaderMenuItemNavLink">
                                            <FormattedMessage {...messages.adminPropertiesLink} />
                                        </Link>
                                    </li>
                                    <li className="HeaderMenuItemSub">
                                        <Link to="/admin/users" className="HeaderMenuItemNavLink">
                                            <FormattedMessage {...messages.adminUsersLink} />
                                        </Link>
                                    </li>
                                    <li className="HeaderMenuItemSub">
                                        <Link to="/admin/budget" className="HeaderMenuItemNavLink">
                                            <FormattedMessage {...messages.adminBudgetLink} />
                                        </Link>
                                    </li>
                                    <li className="HeaderMenuItemSub">
                                        <Link to="/admin/scheduler" className="HeaderMenuItemNavLink">
                                            <FormattedMessage {...messages.adminSchedulerLink} />
                                        </Link>
                                    </li>
                                    <li className="HeaderMenuItemSub">
                                        <Link to="/admin/creation" className="HeaderMenuItemNavLink">
                                            <FormattedMessage {...messages.adminCreationLink} />
                                        </Link>
                                    </li>
                                    <li className="HeaderMenuItemSub">
                                        <Link to="/admin/accounts" className="HeaderMenuItemNavLink">
                                            <FormattedMessage {...messages.adminAccountManagementLink} />
                                        </Link>
                                    </li>
                                    <li className="HeaderMenuItemSub">
                                        <Link to="/admin/notifications" className="HeaderMenuItemNavLink">
                                            <FormattedMessage {...messages.adminNotificationsLink} />
                                        </Link>
                                    </li>
                                </ul>
                            </li>
                        )}
                    </ul>
                    <div className="HeaderMenuRight">
                        {permissions.viewNotifications && (
                            <li className="HeaderMenuItem">
                                <div className="HeaderMenuItemNavLink">
                                    <NotificationWrapper>
                                        <Badge count={notificationCount}>
                                            <Link to="/app/notifications">
                                                <BellOutlined />
                                            </Link>
                                        </Badge>
                                    </NotificationWrapper>
                                </div>
                            </li>
                        )}
                        <li className="HeaderMenuItem">
                            <div className="HeaderMenuItemNavLink">
                                {user.firstName}
                                <div>
                                    <i className="fa fa-cog HeaderMenuItemNavLinkIcon" />
                                    <i className="fa fa-caret-down HeaderMenuItemNavLinkIcon" />
                                </div>
                            </div>
                            <ul>
                                <li className="HeaderMenuItemSub">
                                    <Link to="/logout" className="HeaderMenuItemNavLink">
                                        <FormattedMessage {...messages.logoutLink} />
                                    </Link>
                                </li>
                            </ul>
                        </li>
                    </div>
                </nav>
            </div>
        </>
    );
};

export default header;
